import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import type { StatusOption } from "@/lib/api-types";

/**
 * Unified Status field (migration 0023) — member hooks.
 * Options are workspace-wide; setting a video's status resolves to the stack root.
 */
const STATUS_OPTIONS_KEY = ["status-options"] as const;

export function useStatusOptions() {
  return useQuery({
    queryKey: STATUS_OPTIONS_KEY,
    queryFn: () => api.get<{ options: StatusOption[] }>("/editor/status-options"),
    staleTime: 60_000,
  });
}

/** PATCH /editor/videos/:id/status — set by existing option id, or by label (create-or-reuse). */
export function useSetVideoStatus(projectId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ videoId, statusOptionId, label }: { videoId: string; statusOptionId?: string; label?: string }) =>
      api.patch<{ ok: true; status: StatusOption }>(
        `/editor/videos/${videoId}/status`,
        statusOptionId ? { statusOptionId } : { label },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["projects", projectId, "videos"] });
      qc.invalidateQueries({ queryKey: ["projects"] });
      qc.invalidateQueries({ queryKey: ["videos"] });
      qc.invalidateQueries({ queryKey: STATUS_OPTIONS_KEY });   // a new option may have been created
    },
  });
}
